import React, { useState, useEffect } from 'react';
import {
  getSupplierSettings,
  updateSupplierSettings,
  addSupplierCategory,
  removeSupplierCategory,
  addSupplierSpecialty,
  removeSupplierSpecialty
} from '../lib/supplierSettings';
import {
  BuildingStorefrontIcon,
  TagIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

export default function SupplierCategorySettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [newSpecialty, setNewSpecialty] = useState('');
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showAddSpecialty, setShowAddSpecialty] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await getSupplierSettings();
      setSettings(data);
    } catch (error) {
      console.error('Error loading supplier settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) return;

    try {
      setSaving(true);
      const categoryValue = newCategory.trim().toLowerCase().replace(/\s+/g, '_');
      await addSupplierCategory(categoryValue);
      setNewCategory('');
      setShowAddCategory(false);
      await loadSettings();
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error adding category:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveCategory = async (category) => {
    if (!window.confirm(`Are you sure you want to remove the category "${category}"?`)) {
      return;
    }

    try {
      setSaving(true);
      await removeSupplierCategory(category);
      await loadSettings();
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error removing category:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleAddSpecialty = async (e) => {
    e.preventDefault();
    if (!newSpecialty.trim()) return;

    try {
      setSaving(true);
      await addSupplierSpecialty(newSpecialty.trim());
      setNewSpecialty('');
      setShowAddSpecialty(false);
      await loadSettings();
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error adding specialty:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveSpecialty = async (specialty) => {
    if (!window.confirm(`Are you sure you want to remove the specialty "${specialty}"?`)) {
      return;
    }

    try {
      setSaving(true);
      await removeSupplierSpecialty(specialty);
      await loadSettings();
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error removing specialty:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const formatCategoryLabel = (category) => {
    return category.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500 mx-auto mb-2"></div>
            <p className="text-gray-600">Loading supplier settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <BuildingStorefrontIcon className="w-6 h-6 text-pink-600 mr-3" />
          <h2 className="text-xl font-bold text-gray-900">Supplier Categories & Specialties</h2>
        </div>
        {saveStatus && (
          <div className={`flex items-center px-3 py-1 rounded-lg text-sm ${
            saveStatus === 'success' 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {saveStatus === 'success' ? (
              <>
                <CheckCircleIcon className="w-4 h-4 mr-1" />
                Saved
              </>
            ) : (
              <>
                <XCircleIcon className="w-4 h-4 mr-1" />
                Error
              </>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Categories Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <TagIcon className="w-5 h-5 mr-2 text-blue-600" />
              Primary Categories
            </h3>
            <button
              onClick={() => setShowAddCategory(true)}
              className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Add Category
            </button>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {settings?.categories?.map((category, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="font-medium text-gray-800">
                  {formatCategoryLabel(category)}
                </span>
                <button
                  onClick={() => handleRemoveCategory(category)}
                  disabled={saving}
                  className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                  title="Remove category"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Add Category Form */}
          {showAddCategory && (
            <form onSubmit={handleAddCategory} className="mt-4 p-4 bg-blue-50 rounded-lg">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Category Name
                  </label>
                  <input
                    type="text"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="e.g., Window Treatments"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    disabled={saving}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={saving || !newCategory.trim()}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? 'Adding...' : 'Add Category'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddCategory(false);
                      setNewCategory('');
                    }}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Specialties Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <TagIcon className="w-5 h-5 mr-2 text-green-600" />
              Specialties
            </h3>
            <button
              onClick={() => setShowAddSpecialty(true)}
              className="flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Add Specialty
            </button>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {settings?.specialties?.map((specialty, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="font-medium text-gray-800">
                  {specialty}
                </span>
                <button
                  onClick={() => handleRemoveSpecialty(specialty)}
                  disabled={saving}
                  className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                  title="Remove specialty"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Add Specialty Form */}
          {showAddSpecialty && (
            <form onSubmit={handleAddSpecialty} className="mt-4 p-4 bg-green-50 rounded-lg">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Specialty
                  </label>
                  <input
                    type="text"
                    value={newSpecialty}
                    onChange={(e) => setNewSpecialty(e.target.value)}
                    placeholder="e.g., Outdoor Furniture"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                    disabled={saving}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={saving || !newSpecialty.trim()}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {saving ? 'Adding...' : 'Add Specialty'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddSpecialty(false);
                      setNewSpecialty('');
                    }}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>

      <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
        <h4 className="font-medium text-yellow-800 mb-2">ℹ️ How This Works</h4>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• <strong>Categories</strong> are the main business types (e.g., Upholstery, Furniture, Delivery)</li>
          <li>• <strong>Specialties</strong> are specific skills or services (e.g., Custom Cushions, Rush Delivery)</li>
          <li>• These options will appear in supplier profiles and quote requests</li>
          <li>• Changes are saved automatically and apply to all new suppliers</li>
          <li>• Existing suppliers keep their current categories until manually updated</li>
        </ul>
      </div>
    </div>
  );
}